/**
 * Admin Video Management Service
 * Handles video upload, listing, and deletion for administrators
 */
import axios, { AxiosInstance, AxiosProgressEvent } from 'axios';
import { getConfig } from '../config/Constants';

// ============================================================================
// Types
// ============================================================================

export interface VideoUploadData {
  courseId: string;
  segmentId: string;
  topicId: string;
  title?: string;
  description?: string;
  file: File;
}

export interface VideoUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface VideoUploadResponse {
  success: boolean;
  video_url: string;
  course_id: string;
  segment_id: string;
  topic_id: string;
  size_mb: number;
  upload_time: string;
  message: string;
}

export interface VideoListItem {
  course_id: string;
  course_title: string;
  segment_id: string;
  topic_id?: string;
  segment_title?: string;
  size_mb: number;
  upload_date: string;
  duration?: string;
  s3_key: string;
}

export interface VideoListResponse {
  videos: VideoListItem[];
  total_count: number;
  total_size_gb: number;
}

export interface VideoDeleteRequest {
  courseId: string;
  topicId: string;
}

export interface VideoDeleteResponse {
  success: boolean;
  message: string;
  deleted_key: string;
}

export interface PresignedUrlResponse {
  upload_url: string;
  expires_in_seconds: number;
  course_id: string;
  topic_id: string;
  s3_key?: string;
}

export interface VideoUploadConfirmRequest {
  courseId: string;
  topicId: string;
  s3Key: string;
  fileSize: number;
}

export interface VideoUploadConfirmResponse {
  success: boolean;
  message: string;
  video_url: string;
  size_mb: number;
}


// ============================================================================
// Axios Instance with Interceptors
// ============================================================================

let apiInstance: AxiosInstance | null = null;

function getApiInstance(): AxiosInstance {
  if (!apiInstance) {
    const config = getConfig();

    apiInstance = axios.create({
      baseURL: config.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to add auth token
    apiInstance.interceptors.request.use(
      (config) => {
        // Look for token in localStorage - check multiple possible keys
        const token = localStorage.getItem('access_token') ||
                     localStorage.getItem('token') ||
                     localStorage.getItem('accessToken') ||
                     localStorage.getItem('authToken');

        if (token && token !== 'undefined' && token !== 'null') {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        console.error('[AdminVideoService] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle errors
    apiInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error('[AdminVideoService] Response error:', {
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
        });

        if (error.response?.status === 401) {
          // Token expired or invalid - clear auth and redirect
          console.error('[AdminVideoService] 401 Unauthorized - clearing auth data');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          localStorage.removeItem('token'); // Clear other possible token keys
          window.location.href = '/sign-in';
        } else if (error.response?.status === 403) {
          console.error('[AdminVideoService] 403 Forbidden - admin privileges required');
        }

        return Promise.reject(error);
      }
    );
  }

  return apiInstance;
}

// ============================================================================
// Service Class
// ============================================================================

class AdminVideoService {
  /**
   * Upload a video file with progress tracking
   */
  async uploadVideo(
    data: VideoUploadData,
    onProgress?: (progress: VideoUploadProgress) => void
  ): Promise<VideoUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('course_id', data.courseId);
      formData.append('segment_id', data.segmentId);
      formData.append('topic_id', data.topicId);
      if (data.title) formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      formData.append('file', data.file);

      const response = await getApiInstance().post<VideoUploadResponse>(
        '/admin/videos/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
              const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage,
              });
            }
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('[AdminVideoService] Video upload error:', error);
      if (error.response?.status === 413) {
        throw new Error('File too large. Maximum size is 2GB.');
      }
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to upload video');
    }
  }

  /**
   * Get list of all uploaded videos
   */
  async listVideos(courseId?: string): Promise<VideoListResponse> {
    try {
      const params = courseId ? { course_id: courseId } : {};
      const response = await getApiInstance().get<VideoListResponse>(
        '/admin/videos/list',
        { params }
      );

      return response.data;
    } catch (error: any) {
      console.error('[AdminVideoService] Error listing videos:', error);

      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }

      throw new Error(error.response?.data?.detail || error.message || 'Failed to fetch video list');
    }
  }

  /**
   * Delete a video
   */
  async deleteVideo(request: VideoDeleteRequest): Promise<VideoDeleteResponse> {
    try {
      const response = await getApiInstance().delete<VideoDeleteResponse>(
        '/admin/videos/delete',
        {
          data: {
            course_id: request.courseId,
            topic_id: request.topicId,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('[AdminVideoService] Error deleting video:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to delete video');
    }
  }

  /**
   * Generate a presigned URL for direct upload to S3
   * This is for future enhancement to support very large files
   */
  async generateUploadUrl(
    courseId: string,
    topicId: string
  ): Promise<PresignedUrlResponse> {
    try {
      const formData = new FormData();
      formData.append('course_id', courseId);
      formData.append('topic_id', topicId);

      const response = await getApiInstance().post<PresignedUrlResponse>(
        '/admin/videos/generate-upload-url',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('[AdminVideoService] Error generating upload URL:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to generate upload URL');
    }
  }

  /**
   * Upload directly to S3 using presigned URL
   * This is for future enhancement
   */
  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: VideoUploadProgress) => void
  ): Promise<void> {
    const maxRetries = 3;
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // For S3 upload, use plain axios without auth headers
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'video/mp4',
          },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress && progressEvent.total) {
              const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage,
              });
            }
          },
          timeout: 0, // No timeout for large files
        });
        return; // Success - exit retry loop

      } catch (error: any) {
        lastError = error;
        console.error(`[AdminVideoService] S3 upload attempt ${attempt} failed:`, error);

        // Don't retry on certain errors
        if (error.response?.status === 403 || error.response?.status === 404) {
          throw new Error('Upload URL expired or invalid. Please try again.');
        }

        // If this isn't the last attempt, wait before retrying
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    console.error('[AdminVideoService] All S3 upload attempts failed');
    throw new Error(
      lastError?.message || 
      'Failed to upload video after multiple attempts. Please check your connection and try again.'
    );
  }





  /**
   * Upload video using presigned URL flow (enterprise-grade)
   * This is the recommended method for all video uploads
   * 
   * Flow:
   * 1. Request presigned URL from backend
   * 2. Upload directly to S3
   * 3. Confirm upload with backend
   */
  async uploadVideoPresigned(
    data: VideoUploadData,
    onProgress?: (progress: VideoUploadProgress) => void,
    onStatusChange?: (status: string) => void
  ): Promise<VideoUploadResponse> {
    try {
      // Step 1: Request presigned URL
      onStatusChange?.('Requesting upload URL...');
      const urlResponse = await this.generateUploadUrl(
        data.courseId,
        data.topicId
      );

      if (!urlResponse.upload_url || !urlResponse.s3_key) {
        throw new Error('Invalid presigned URL response');
      }
      onStatusChange?.('Uploading to cloud storage...');

      // Step 2: Upload directly to S3
      await this.uploadToS3(
        urlResponse.upload_url,
        data.file,
        onProgress
      );
      onStatusChange?.('Verifying upload...');

      // Step 3: Confirm upload with backend
      const confirmResponse = await this.confirmUpload({
        courseId: data.courseId,
        topicId: data.topicId,
        s3Key: urlResponse.s3_key,
        fileSize: data.file.size,
      });
      onStatusChange?.('Upload complete!');

      // Return response in VideoUploadResponse format
      return {
        success: confirmResponse.success,
        video_url: confirmResponse.video_url,
        course_id: data.courseId,
        segment_id: data.segmentId || '',
        topic_id: data.topicId,
        size_mb: confirmResponse.size_mb,
        upload_time: new Date().toISOString(),
        message: confirmResponse.message,
      };

    } catch (error: any) {
      console.error('[AdminVideoService] Presigned upload error:', error);
      
      // Provide detailed error messages
      if (error.message.includes('presigned URL')) {
        throw new Error('Failed to get upload URL. Please try again.');
      }
      if (error.message.includes('S3') || error.message.includes('storage')) {
        throw new Error('Failed to upload to storage. Please check your connection and try again.');
      }
      if (error.message.includes('confirm')) {
        throw new Error('Upload succeeded but confirmation failed. Please contact support.');
      }
      
      throw error;
    }
  }

  /**
   * Confirm video upload to backend after successful S3 upload
   */
  async confirmUpload(
    request: VideoUploadConfirmRequest
  ): Promise<VideoUploadConfirmResponse> {
    try {
      const response = await getApiInstance().post<VideoUploadConfirmResponse>(
        '/admin/videos/confirm-upload',
        {
          course_id: request.courseId,
          topic_id: request.topicId,
          s3_key: request.s3Key,
          file_size: request.fileSize,
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('[AdminVideoService] Error confirming upload:', error);
      if (error.response?.status === 403) {
        throw new Error('Access denied - admin privileges required');
      }
      if (error.response?.status === 401) {
        throw new Error('Not authenticated - please log in again');
      }
      throw new Error(error.response?.data?.detail || error.message || 'Failed to confirm upload');
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Validate video file
   */
  validateVideoFile(file: File): { valid: boolean; error?: string } {
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: MP4, WebM, MOV, AVI`,
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File too large. Maximum size is 2GB (your file: ${this.formatFileSize(file.size)})`,
      };
    }

    return { valid: true };
  }
}

// ============================================================================
// Export Singleton
// ============================================================================

export const adminVideoService = new AdminVideoService();